import { formatPrice } from '../utils/formatters.js';

/**
 * Layout of the gift card image (spec staff-giftcards B3, direction contract
 * `.impeccable/surfaces/js-components-giftcards-giftcardimage-js.md`): the
 * bar's comanda, already paid. Pure: no canvas, no DOM. The ticket text is
 * Courier Prime, a monospaced face, so every line's width is
 * `chars × ADVANCE × size + (chars − 1) × tracking` exactly, and the limits
 * (60-character names, 200-character message, $1.000.000) are checked in
 * unit tests without a browser. `GiftcardImage.js` only paints this plan.
 */

export const CANVAS = { width: 1080, height: 1350 };

/** Courier Prime advance width, in em (every glyph). */
export const ADVANCE = 0.6;

export const COLORS = {
    fondo: '#14532d',
    papel: '#fbfaf6',
    tinta: '#222222',
    tenue: '#6b6b66',
    regla: '#8a8a84',
    sello: 'rgba(20, 83, 45, 0.82)',
};

const TICKET_WIDTH = 730;
const TICKET_MIN_HEIGHT = 760;
const PAD_X = 66;
const PAD_TOP = 70;
const PAD_BOTTOM = 110; // room above the zigzag tear
const INNER = TICKET_WIDTH - PAD_X * 2;
const MARGIN_Y = 60; // ticket ↔ canvas edge, before the tilt
const LOGO = { width: 420, ratio: 3276 / 1312 };

/** Line height factor for the ticket text. */
const LEADING = 1.45;

/** @param {string} text @param {number} size @param {number} [tracking] */
export function textWidth(text, size, tracking = 0) {
    const n = [...text].length;
    return n === 0 ? 0 : n * ADVANCE * size + (n - 1) * tracking;
}

/** Characters that fit in `width` at `size` with `tracking`. */
function charsPerLine(width, size, tracking = 0) {
    return Math.max(1, Math.floor((width + tracking) / (ADVANCE * size + tracking)));
}

/**
 * Word wrap for a monospaced face; a word longer than a line is split.
 * @param {string} text @param {number} size @param {number} width @param {number} [tracking]
 * @returns {string[]}
 */
export function wrap(text, size, width, tracking = 0) {
    const max = charsPerLine(width, size, tracking);
    const lines = [];
    let line = '';
    for (const word of String(text).trim().split(/\s+/).filter(Boolean)) {
        let rest = [...word];
        while (rest.length > max) {
            if (line) {
                lines.push(line);
                line = '';
            }
            lines.push(rest.slice(0, max).join(''));
            rest = rest.slice(max);
        }
        const w = rest.join('');
        if (!w) continue;
        const candidate = line ? `${line} ${w}` : w;
        if ([...candidate].length <= max) {
            line = candidate;
        } else {
            lines.push(line);
            line = w;
        }
    }
    if (line) lines.push(line);
    return lines;
}

/** `$30.000`: the site's money format (`formatPrice`), whole pesos. */
export function formatMontoTicket(monto) {
    return formatPrice(Math.round(Number(monto)));
}

/** Letters the stamp can hold around "PAGADO POR". */
const STAMP_NAME_MAX = 14;

/**
 * @param {{ para: string, deParte: string | null, mensaje: string | null, monto: number, validaHasta: string }} model
 * @returns {{
 *   canvas: { width: number, height: number },
 *   ticket: { x: number, y: number, width: number, height: number, scale: number, rotation: number },
 *   logo: { x: number, y: number, width: number, height: number },
 *   texts: Array<{ text: string, x: number, y: number, size: number, bold: boolean, align: 'left'|'right'|'center', tracking: number, color: string }>,
 *   rules: Array<{ kind: 'dash'|'dots', x1: number, x2: number, y: number }>,
 *   stamp: { cx: number, cy: number, r: number, lines: string[], rotation: number },
 *   inner: { left: number, right: number },
 * }}
 */
export function layoutGiftcard(model) {
    /** @type {ReturnType<typeof layoutGiftcard>['texts']} */
    const texts = [];
    /** @type {ReturnType<typeof layoutGiftcard>['rules']} */
    const rules = [];
    const left = PAD_X;
    const right = PAD_X + INNER;
    let y = PAD_TOP;

    const logo = { x: (TICKET_WIDTH - LOGO.width) / 2, y, width: LOGO.width, height: LOGO.width / LOGO.ratio };
    y += logo.height + 22;

    const add = (text, { size, bold = false, align = 'left', tracking = 0, color = COLORS.tinta, top = y }) => {
        const anchor = align === 'right' ? right : align === 'center' ? TICKET_WIDTH / 2 : left;
        texts.push({ text, x: anchor, y: top, size, bold, align, tracking, color });
    };
    const dash = (gapBefore = 34, gapAfter = 44) => {
        y += gapBefore;
        rules.push({ kind: 'dash', x1: left, x2: right, y });
        y += gapAfter;
    };

    add('GIFT CARD', { size: 28, align: 'center', tracking: 6, color: COLORS.tinta });
    y += 28 * LEADING;
    dash();

    // "MESA <para>", "INVITA <de parte de>": label and value on one line when
    // they fit; otherwise the label alone and the value wrapped below it.
    const row = (label, value) => {
        const size = 38;
        const labelW = textWidth(label, size);
        if (labelW + 28 + textWidth(value, size) <= INNER) {
            add(label, { size });
            add(value, { size, bold: true, align: 'right' });
            y += size * LEADING;
            return;
        }
        add(label, { size });
        y += size * LEADING;
        for (const line of wrap(value, size, INNER)) {
            add(line, { size, bold: true });
            y += size * LEADING;
        }
    };
    row('MESA', model.para.toUpperCase());
    if (model.deParte) row('INVITA', model.deParte.toUpperCase());
    dash(24);

    // "1 x Lo que quieras ......"
    const item = '1 x Lo que quieras';
    add(item, { size: 38 });
    rules.push({ kind: 'dots', x1: left + textWidth(item, 38) + 14, x2: right, y: y + 38 * 0.82 });
    y += 38 * LEADING + 6;

    // "TOTAL A FAVOR  $30.000": the amount shares the line while it fits at
    // its full size, else it takes its own line under the label.
    const monto = formatMontoTicket(model.monto);
    const label = 'TOTAL A FAVOR';
    const labelSize = 30;
    const amountSize = 76;
    const amountTracking = -3;
    const sameLine = textWidth(label, labelSize, 1) + 24 + textWidth(monto, amountSize, amountTracking) <= INNER;
    if (sameLine) {
        // The label sits on the amount's baseline, not on its top.
        add(label, { size: labelSize, bold: true, tracking: 1, top: y + (amountSize - labelSize) * 0.78 });
        add(monto, { size: amountSize, bold: true, align: 'right', tracking: amountTracking });
        y += amountSize * 1.15;
    } else {
        add(label, { size: labelSize, bold: true, tracking: 1 });
        y += labelSize * LEADING;
        add(monto, { size: amountSize, bold: true, align: 'right', tracking: amountTracking });
        y += amountSize * 1.15;
    }
    dash(26);

    if (model.mensaje) {
        add('NOTA', { size: 28, tracking: 5, color: COLORS.tenue });
        y += 28 * LEADING + 4;
        for (const line of wrap(model.mensaje, 36, INNER)) {
            add(line, { size: 36 });
            y += 36 * LEADING;
        }
        y += 30;
    }

    for (const line of [`VÁLIDA HASTA EL ${model.validaHasta}`, 'CANJEABLE EN PANACHE']) {
        add(line, { size: 30, align: 'center', tracking: 4 });
        y += 30 * LEADING;
    }

    const height = Math.max(TICKET_MIN_HEIGHT, Math.ceil(y + PAD_BOTTOM));
    const scale = Math.min(1, (CANVAS.height - MARGIN_Y * 2) / height);
    const ticket = {
        width: TICKET_WIDTH,
        height,
        scale,
        rotation: (-2.2 * Math.PI) / 180,
        x: (CANVAS.width - TICKET_WIDTH * scale) / 2,
        y: (CANVAS.height - height * scale) / 2,
    };

    const nombre = model.deParte?.toUpperCase() ?? '';
    const stamp = {
        cx: TICKET_WIDTH - 140,
        cy: logo.y + logo.height + 40,
        r: 96,
        rotation: (14 * Math.PI) / 180,
        lines: nombre && [...nombre].length <= STAMP_NAME_MAX ? ['PAGADO', 'POR', nombre] : ['PAGADO'],
    };

    return { canvas: CANVAS, ticket, logo, texts, rules, stamp, inner: { left, right } };
}
